import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const BRAND_LOGO =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_bpremium-shop/artifacts/mvmypmho_1.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/catalog", label: "Catalog" },
  { to: "/#story", label: "Story" },
  { to: "/#contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname, loc.hash]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-ivory/80 backdrop-blur-xl border-b border-navy/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3"
          aria-label="B'Premium home"
        >
          <img
            src={BRAND_LOGO}
            alt="B'Premium"
            className="h-7 md:h-9 w-auto"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.18em] uppercase font-medium link-underline ${
                  isActive && l.to === loc.pathname
                    ? "text-gold"
                    : "text-navy/75 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/admin/login"
            data-testid="header-admin-link"
            className="text-[11px] tracking-[0.25em] uppercase text-navy/40 hover:text-gold transition-colors"
          >
            Admin
          </Link>
        </nav>

        {/* Mobile trigger */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-sm border border-navy/20 text-navy"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden border-t border-navy/10 bg-ivory/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="text-lg font-display text-navy"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/admin/login"
                data-testid="mobile-nav-admin"
                className="text-xs tracking-[0.25em] uppercase text-gold pt-2 border-t border-navy/10"
              >
                Admin →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
